import type { MutableRefObject } from 'react'
import { useCallback, useLayoutEffect, useRef } from 'react'

/** A quick way to sync data to a ref. */
export function useSyncedRef<T>(item: T): MutableRefObject<T> {
  const ref = useRef(item)

  useLayoutEffect(() => {
    ref.current = item
  }, [item])

  return ref
}

/**
 * A memory-stable way to declare a callback. Inspired by react's RFC:
 * https://github.com/reactjs/rfcs/pull/220
 */
export function useEvent<F extends (...args: Parameters<F>) => ReturnType<F>>(cb?: F): F {
  const cbRef = useSyncedRef(cb)
  const result = useCallback((...args: Parameters<F>) => cbRef.current?.(...args), [cbRef])

  return result as F
}
